import React, { useState, useEffect, useRef } from 'react';
import { ProcurementProfileData } from '@/api/entities';
import { ServerCrash } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/components/utils/translations';
import { ProfileDataSection } from '@/components/accounts/ProfileSectionDisplay';
import { recordMatchesAccount } from '@/lib/accountPoolJoin';

/** Extract section order for sorting: supports "Section 0 Identity", "Section 10 Sustainability", "0 Core profile", etc. */
const getSectionOrder = (title) => {
  const sectionMatch = title.match(/Section\s+(\d+)/i);
  if (sectionMatch) return parseInt(sectionMatch[1], 10);
  const prefixMatch = title.match(/^(\d+)\s/);
  if (prefixMatch) return parseInt(prefixMatch[1], 10);
  return null;
};

const sortSectionsByOrder = (a, b) => {
  const orderA = getSectionOrder(typeof a === 'string' ? a : a.sectionTitle ?? a[0]);
  const orderB = getSectionOrder(typeof b === 'string' ? b : b.sectionTitle ?? b[0]);
  const titleA = typeof a === 'string' ? a : (a.sectionTitle ?? a[0]);
  const titleB = typeof b === 'string' ? b : (b.sectionTitle ?? b[0]);
  if (orderA != null && orderB != null) return orderA - orderB;
  if (orderA != null) return -1;
  if (orderB != null) return 1;
  return String(titleA).localeCompare(String(titleB));
};

export default function ProcurementProfile({ accountName, accountId, globalPoolAccountId, profileData: profileDataProp }) {
  const { t } = useTranslation();
  const [procurementData, setProcurementData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const profileDataPropRef = useRef(profileDataProp);
  profileDataPropRef.current = profileDataProp;

  // Use prefetched profileData from AccountFinal when available (includes learnwise+global merge)
  useEffect(() => {
    if (profileDataProp) {
      const data = profileDataProp?.profile_data ?? profileDataProp;
      setProcurementData(data);
      setIsLoading(false);
      setError(null);
      return;
    }
  }, [profileDataProp]);

  useEffect(() => {
    if (profileDataProp) return; // Skip fetch when parent provides data
    if (!accountName && !accountId) {
        setIsLoading(false);
        setError("No account identifier provided.");
        return;
    }

    const fetchProfile = async () => {
        setIsLoading(true);
        setError(null);
        try {
            console.log('🔍 ProcurementProfile: Searching for profile with:', { accountName, accountId });
            
            // Try multiple search strategies
            let results = [];

            if (globalPoolAccountId) {
                results = await ProcurementProfileData.filter({ account_id: globalPoolAccountId });
                console.log('📊 Results by global_pool_account_id:', results.length);
            }

            if (results.length === 0 && accountId) {
                results = await ProcurementProfileData.filter({ account_id: accountId });
                console.log('📊 Results by account_id:', results.length);
            }

            if (results.length === 0 && accountName) {
                results = await ProcurementProfileData.filter({ account_name: accountName });
                console.log('📊 Results by account_name:', results.length);
            }
            
            // Strategy 3: If still no results, get all and filter manually
            if (results.length === 0) {
                const allProfiles = await ProcurementProfileData.list();
                console.log('📊 All procurement profiles:', allProfiles.length);
                
                results = allProfiles.filter((p) =>
                    recordMatchesAccount(p, { accountId, globalPoolAccountId, accountName })
                );
                
                console.log('📊 Manually filtered results:', results.length);
                
                // Debug: Show what we have
                setDebugInfo({
                    searchedFor: { accountName, accountId },
                    totalProfiles: allProfiles.length,
                    availableNames: allProfiles.map(p => ({ id: p.account_id, name: p.account_name })).slice(0, 10)
                });
            }
            
            if (profileDataPropRef.current) return; // Parent provided data, don't overwrite
            if (results && results.length > 0) {
                console.log('✅ Found procurement profile:', results[0].account_name);
                setProcurementData(results[0].profile_data);
            } else {
                console.log('❌ No procurement profile found');
                setProcurementData(null);
            }
        } catch (err) {
            console.error("Failed to fetch procurement profile:", err);
            setError("Could not load procurement profile.");
        } finally {
            if (!profileDataPropRef.current) setIsLoading(false);
        }
    };

    fetchProfile();
  }, [accountName, accountId, globalPoolAccountId, profileDataProp]);

  if (isLoading) {
    return (
        <div className="space-y-4">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-40 w-full" />
        </div>
    );
  }

  if (error) {
    return (
        <div className="text-center py-10 text-red-500">
            <ServerCrash className="w-8 h-8 mx-auto mb-2" />
            <p>{error}</p>
        </div>
    );
  }

  if (!procurementData) {
    return (
        <div className="text-center py-10">
            <p className="text-gray-500 mb-4">{t('procurementProfileNA')}</p>
            {debugInfo && (
                <details className="text-left max-w-2xl mx-auto bg-gray-50 p-4 rounded text-xs">
                    <summary className="cursor-pointer font-semibold mb-2">Debug Info</summary>
                    <pre className="overflow-auto">{JSON.stringify(debugInfo, null, 2)}</pre>
                </details>
            )}
        </div>
    );
  }

  // Transform the new nested structure into displayable sections
  const renderSections = () => {
    if (!procurementData || typeof procurementData !== 'object') {
      return null;
    }

    // Check if it's the new nested format with rows array
    if (procurementData.rows && Array.isArray(procurementData.rows)) {
      const sections = new Map();
      
      procurementData.rows.forEach(row => {
        const section = row.section || 'General Information';
        if (!sections.has(section)) {
          sections.set(section, []);
        }
        sections.get(section).push({
          field: row.field || row['Field / Metric'] || '—',
          value: row.value || row['Value'] || '—',
          source: row.source || row['Source'] || '—',
          url: row.url || row['URL(s)'] || '—',
          confidence: row.confidence || row['Confidence'] || 'M'
        });
      });
      
      const sortedSections = Array.from(sections.entries()).sort(([titleA], [titleB]) =>
        sortSectionsByOrder(titleA, titleB)
      );
      
      return sortedSections.map(([sectionTitle, rows]) => (
        <ProfileDataSection
          key={sectionTitle}
          title={sectionTitle}
          data={rows}
          t={t}
        />
      ));
    }

    // Check if it's the new flat array format with rows
    if (Array.isArray(procurementData)) {
      const sections = new Map();
      
      procurementData.forEach(row => {
        const section = row.section || 'General Information';
        if (!sections.has(section)) {
          sections.set(section, []);
        }
        sections.get(section).push({
          field: row.field || row['Field / Metric'] || '—',
          value: row.value || row['Value'] || '—',
          source: row.source || row['Source'] || '—',
          url: row.url || row['URL(s)'] || '—',
          confidence: row.confidence || row['Confidence'] || 'M'
        });
      });
      
      const sortedSections = Array.from(sections.entries()).sort(([titleA], [titleB]) =>
        sortSectionsByOrder(titleA, titleB)
      );
      
      return sortedSections.map(([sectionTitle, rows]) => (
        <ProfileDataSection
          key={sectionTitle}
          title={sectionTitle}
          data={rows}
          t={t}
        />
      ));
    }

    const sectionEntries = Object.entries(procurementData).map(([sectionKey, sectionValue]) => {
      // Format the section title (convert snake_case to Title Case)
      const sectionTitle = sectionKey
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Skip if section value is not an object or is empty
      if (!sectionValue || typeof sectionValue !== 'object' || Object.keys(sectionValue).length === 0) {
        return null;
      }

      // Transform section data into rows for the table
      const rows = Object.entries(sectionValue)
        .filter(([key, value]) => {
          // Skip metadata keys
          return key !== 'confidence' && value !== null && value !== undefined;
        })
        .map(([fieldKey, fieldValue]) => {
          // Format field name
          const fieldName = fieldKey
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          // Handle different value types
          let displayValue = '';
          let source = '—';
          let url = '—';
          let confidence = sectionValue.confidence || 'M';

          if (typeof fieldValue === 'object' && fieldValue !== null) {
            // Handle objects with value/source/url/confidence
            if (fieldValue.value !== undefined) {
              displayValue = String(fieldValue.value || '—');
              if (fieldValue.description) {
                displayValue += ` (${fieldValue.description})`;
              }
              source = fieldValue.source || '—';
              url = fieldValue.url || '—';
              confidence = fieldValue.confidence || confidence;
            } else if (Array.isArray(fieldValue)) {
              displayValue = fieldValue.join(', ') || '—';
            } else {
              displayValue = JSON.stringify(fieldValue, null, 2);
            }
          } else {
            displayValue = String(fieldValue || '—');
          }

          return {
            field: fieldName,
            value: displayValue,
            source,
            url,
            confidence
          };
        });

      if (rows.length === 0) return null;

      return {
        sectionKey,
        sectionTitle,
        rows
      };
    }).filter(Boolean);

    sectionEntries.sort((a, b) => sortSectionsByOrder(a, b));

    return sectionEntries.map(({ sectionKey, sectionTitle, rows }) => (
      <ProfileDataSection
        key={sectionKey}
        title={sectionTitle}
        data={rows}
        t={t}
      />
    ));
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-sm text-[#5C6B7A]">
          {t('procurementIntelligence')}
        </p>
      </div>
      
      {renderSections()}
    </div>
  );
}